import React, { useState } from 'react';
import Layout from '../../components/Layout/Layout';
import { FiMail, FiPhone, FiClock, FiMessageCircle } from '../../components/Icons';
import { config } from '../../config/config';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    category: 'general',
    urgency: 'medium'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      // Map frontend categories to backend categories
      const categoryMap: { [key: string]: string } = {
        'general': 'general',
        'technical': 'technical',
        'billing': 'billing',
        'feature': 'feature-request',
        'training': 'training',
        'bug': 'bug-report'
      };

      const response = await fetch(`${config.apiUrl}/support/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || undefined,
          category: categoryMap[formData.category] || 'general',
          subject: formData.subject || undefined,
          message: formData.message,
          urgency: formData.urgency
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus({
          type: 'success',
          message: `Thank you for contacting us! Your ticket ID is ${data.ticketId}. We will respond within 24 hours.`
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          category: 'general',
          urgency: 'medium'
        });
      } else {
        throw new Error(data.message || 'Failed to submit support message');
      }
    } catch (error) {
      console.error('Error submitting support message:', error);
      setSubmitStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to submit your message. Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Contact Support
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We're here to help! Reach out with any questions or issues you're experiencing with the CRM.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              
              {/* Status Messages */}
              {submitStatus.type && (
                <div
                  className={`mb-6 p-4 rounded-lg ${
                    submitStatus.type === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 mb-2">
                    Priority Level
                  </label>
                  <select
                    id="urgency"
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">Low - General inquiry</option>
                    <option value="medium">Medium - Need help soon</option>
                    <option value="high">High - Business impacted</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="general">General Question</option>
                    <option value="technical">Technical Issue</option>
                    <option value="billing">Billing Question</option>
                    <option value="feature">Feature Request</option>
                    <option value="bug">Bug Report</option>
                    <option value="training">Training Request</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Please describe your question or issue in detail..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full px-6 py-3 rounded-lg transition-colors font-medium ${
                    isSubmitting
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            {/* Direct Contact */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Direct Contact</h3>
              <div className="space-y-4">
                <a
                  href="mailto:support@thedefinedcrm.com"
                  className="flex items-start space-x-3 text-gray-600 hover:text-gray-900"
                >
                  <FiMail className="h-5 w-5 mt-0.5" />
                  <div>
                    <p className="font-medium">Email Support</p>
                    <p className="text-sm">support@thedefinedcrm.com</p>
                  </div>
                </a>
                <div className="flex items-start space-x-3 text-gray-600">
                  <FiClock className="h-5 w-5 mt-0.5" />
                  <div>
                    <p className="font-medium">Support Hours</p>
                    <p className="text-sm">Mon-Fri: 8:00 AM - 6:00 PM PST</p>
                    <p className="text-sm">Sat: 9:00 AM - 3:00 PM PST</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Response Time */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Expected Response Time</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center space-x-2">
                  <span className="text-green-500">●</span>
                  <span><strong>Email:</strong> Within 24 hours</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="text-green-500">●</span>
                  <span><strong>High Priority:</strong> Within 4 hours</span>
                </li>
              </ul>
            </div>

            {/* FAQ Link */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <FiMessageCircle className="h-6 w-6 text-gray-400 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Frequently Asked Questions
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Find instant answers to common questions
                  </p>
                  <a
                    href="/faq"
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Browse FAQ →
                  </a>
                </div>
              </div>
            </div>

            {/* High Priority Support */}
            <div className="bg-red-50 rounded-lg p-6 border border-red-200">
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                High Priority Support
              </h3>
              <p className="text-sm text-red-700 mb-3">
                For critical system issues affecting your business operations:
              </p>
              <a
                href="mailto:support@thedefinedcrm.com?subject=URGENT%20-%20Critical%20Issue"
                className="inline-flex items-center space-x-2 text-red-600 hover:text-red-800 font-medium"
              >
                <FiMail className="h-4 w-4" />
                <span>Send High Priority Email</span>
              </a>
            </div>
          </div>
        </div>

        {/* Additional Resources */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-3xl mb-3">📚</div>
            <h3 className="font-semibold text-gray-900 mb-2">Documentation</h3>
            <p className="text-sm text-gray-600 mb-3">
              Browse our comprehensive guides
            </p>
            <a href="/" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              View Docs →
            </a>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-3xl mb-3">🎥</div>
            <h3 className="font-semibold text-gray-900 mb-2">Video Tutorials</h3>
            <p className="text-sm text-gray-600 mb-3">
              Watch step-by-step tutorials
            </p>
            <a href="/tutorials" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              Watch Videos →
            </a>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-3xl mb-3">💬</div>
            <h3 className="font-semibold text-gray-900 mb-2">Community Forum</h3>
            <p className="text-sm text-gray-600 mb-3">
              Connect with other users
            </p>
            <a href="/community" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              Join Forum →
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ContactPage;